import { updateSettings } from './db';
import { loadAudioManifest } from './vocabulary';

export const AUDIO_CACHE_NAME = 'mnemoflux-audio-v2';
const LEGACY_AUDIO_CACHE_NAMES = ['mnemoflux-audio-v1'];

export interface OfflineProgress {
  cached: number;
  total: number;
  percent: number;
}

export function normalizeAudioCacheKey(value: string): string {
  const url = new URL(value, 'https://mnemoflux.local');
  return `${url.pathname}${url.search}`;
}

export function countCachedManifestPaths(paths: string[], cachedPaths: Iterable<string>): number {
  const available = new Set(Array.from(cachedPaths, normalizeAudioCacheKey));
  return paths.reduce((count, path) => count + (available.has(normalizeAudioCacheKey(path)) ? 1 : 0), 0);
}

export async function initializeAudioCacheVersion(): Promise<void> {
  if (typeof caches === 'undefined') return;
  const deleted = await Promise.all(LEGACY_AUDIO_CACHE_NAMES.map((name) => caches.delete(name)));
  if (deleted.some(Boolean)) {
    await updateSettings({ offlineAudioComplete: false, offlineAudioCached: 0 });
  }
}

export function requiredRemainingBytes(totalBytes: number | undefined, cached: number, total: number): number | undefined {
  if (!totalBytes || total <= 0) return undefined;
  return Math.ceil(totalBytes * Math.max(0, total - cached) / total * 1.08);
}

export async function estimateOfflineStorage(): Promise<{
  quota?: number;
  usage?: number;
  available?: number;
}> {
  if (!navigator.storage?.estimate) return {};
  const { quota, usage } = await navigator.storage.estimate();
  return { quota, usage, available: quota && usage !== undefined ? quota - usage : undefined };
}

export async function cacheAllAudio(
  signal: AbortSignal,
  onProgress: (progress: OfflineProgress) => void,
): Promise<void> {
  const manifest = await loadAudioManifest();
  await initializeAudioCacheVersion();
  const cache = await caches.open(AUDIO_CACHE_NAME);
  const existing = new Set((await cache.keys()).map((request) => normalizeAudioCacheKey(request.url)));
  let cached = countCachedManifestPaths(manifest.paths, existing);
  onProgress({ cached, total: manifest.count, percent: Math.round((cached / manifest.count) * 100) });

  const pending = manifest.paths.filter((path) => !existing.has(normalizeAudioCacheKey(path)));
  const storage = await estimateOfflineStorage();
  const required = requiredRemainingBytes(manifest.totalBytes, cached, manifest.count);
  if (required !== undefined && storage.available !== undefined && storage.available < required) {
    throw new Error(`存储空间不足：仍需约 ${Math.ceil(required / 1024 / 1024)} MB`);
  }
  const concurrency = 6;
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < pending.length) {
      if (signal.aborted) throw new DOMException('Download paused', 'AbortError');
      const path = pending[cursor++];
      const response = await fetch(path, { signal });
      if (!response.ok) throw new Error(`音频下载失败：${path}`);
      await cache.put(path, response);
      cached += 1;
      if (cached % 20 === 0 || cached === manifest.count) {
        await updateSettings({ offlineAudioCached: cached });
        onProgress({
          cached,
          total: manifest.count,
          percent: Math.round((cached / manifest.count) * 100),
        });
      }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  await updateSettings({ offlineAudioComplete: true, offlineAudioCached: manifest.count });
}

export async function verifyAudioCache(): Promise<OfflineProgress> {
  const manifest = await loadAudioManifest();
  await initializeAudioCacheVersion();
  const cache = await caches.open(AUDIO_CACHE_NAME);
  const keys = await cache.keys();
  const cached = countCachedManifestPaths(
    manifest.paths,
    keys.map((request) => normalizeAudioCacheKey(request.url)),
  );
  const progress = { cached, total: manifest.count, percent: Math.round((cached / manifest.count) * 100) };
  await updateSettings({
    offlineAudioCached: cached,
    offlineAudioComplete: cached >= manifest.count,
  });
  return progress;
}
